import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../adminDataProvider';

type ToggleOperatorsStatusParams = {
  channelUrl: string;
  userIds: string[];
  isOperator: boolean;
};

export const useToggleOperatorsStatus = (
  options?: UseMutationOptions<void, Error, ToggleOperatorsStatusParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggle-operators-status'],
    mutationFn: ({
      channelUrl,
      userIds,
      isOperator,
    }: ToggleOperatorsStatusParams) =>
      dataProvider.patchOperators(channelUrl, { userIds, isOperator }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        // Invalidate member and operator queries so lists refresh after operator add/remove
        queryClient.invalidateQueries({
          queryKey: ['channel-members'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['channel-operators'],
        }),
      ]);

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: async (error, variables, onMutateResult, context) => {
      notify(translate('chat.toggleOperatorsStatusError'), {
        autoHideDuration: 5000,
      });
      await options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};
